import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, TrendingUp, Calendar, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { TargetProgress } from '@/types/salesDashboard';

// Format currency in Indian format
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Get progress color based on percentage
const getProgressColor = (progress: number) => {
  if (progress >= 100) return 'bg-green-500';
  if (progress >= 80) return 'bg-blue-500';
  if (progress >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getProgressTextColor = (progress: number) => {
  if (progress >= 100) return 'text-green-600';
  if (progress >= 80) return 'text-blue-600';
  if (progress >= 50) return 'text-yellow-600';
  return 'text-red-600';
};

interface TargetAchievementChartProps {
  targetProgress: TargetProgress;
  isLoading?: boolean;
  /** Bookings (MTD) — sum of dealValue for leads WON this month */
  bookings?: number;
  /** Collections (MTD) — cash received this month from paid invoices */
  collections?: number;
}

export const TargetAchievementChart: React.FC<TargetAchievementChartProps> = ({
  targetProgress,
  isLoading,
  bookings,
  collections,
}) => {
  const navigate = useNavigate();
  const progress = Math.min(targetProgress.percentage, 100);
  const exceededAmount = Math.max(0, targetProgress.achieved - targetProgress.target);

  // Get current month name for display
  const currentMonthName = new Date().toLocaleString('en-IN', { month: 'long' });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Monthly Target Achievement</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 bg-gray-100 animate-pulse rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  // Show a clear message when no targets are configured for the current month
  if (targetProgress.target === 0 && targetProgress.achieved === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-red-600" />
            Monthly Target Achievement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="p-3 bg-amber-50 rounded-full mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              No targets set for {currentMonthName}
            </h3>
            <p className="text-sm text-gray-500 mb-4 max-w-xs">
              {targetProgress.targetsMissing
                ? `Revenue targets haven't been configured for ${currentMonthName} yet. Set them to track your team's progress.`
                : `Configure revenue targets to track monthly achievement.`}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={() => navigate('/revenue-targets')}
            >
              <Target className="w-4 h-4 mr-2" />
              Set Revenue Targets
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Target className="w-5 h-5 text-red-600" />
          Monthly Target Achievement
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Progress Circle */}
          <div className="flex items-center justify-center lg:w-1/3">
            <div className="relative w-40 h-40">
              {/* Background circle */}
              <svg className="w-full h-full transform -rotate-90" role="img" aria-label={`Target achievement: ${targetProgress.percentage.toFixed(1)}% achieved, ${formatCurrency(targetProgress.achieved)} of ${formatCurrency(targetProgress.target)}`}>
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="transparent"
                  className="text-gray-200"
                />
                {/* Progress circle */}
                <motion.circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="transparent"
                  strokeLinecap="round"
                  className={getProgressColor(targetProgress.percentage).replace('bg-', 'text-')}
                  strokeDasharray={`${2 * Math.PI * 70}`}
                  initial={{ strokeDashoffset: 2 * Math.PI * 70 }}
                  animate={{
                    strokeDashoffset:
                      2 * Math.PI * 70 * (1 - Math.min(progress, 100) / 100),
                  }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                />
              </svg>
              {/* Center text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className={`text-3xl font-bold ${getProgressTextColor(targetProgress.percentage)}`}
                >
                  {targetProgress.percentage.toFixed(1)}%
                </motion.span>
                <span className="text-sm text-gray-500">Achieved</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex-1 space-y-4">
            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Progress</span>
                <span className={`font-medium ${getProgressTextColor(targetProgress.percentage)}`}>
                  {formatCurrency(targetProgress.achieved)} / {formatCurrency(targetProgress.target)}
                </span>
              </div>
              <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(progress, 100)}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  className={`absolute inset-y-0 left-0 ${getProgressColor(targetProgress.percentage)} rounded-full`}
                />
              </div>
              {(bookings !== undefined || collections !== undefined) && (() => {
                const booked = bookings ?? 0;
                const collected = collections ?? 0;
                const gap = collected - booked;
                const gapLabel =
                  gap >= 0 ? 'prior-month collections' : 'receivables outstanding';
                const gapSign = gap >= 0 ? '+' : '-';
                const gapAbs = Math.abs(gap);
                return (
                  <p
                    className="text-xs text-gray-500 mt-2"
                    title="Booked = deals WON this month. Collected = cash received this month. Gap = Collected - Booked."
                  >
                    Booked {formatCurrency(booked)} · Collected {formatCurrency(collected)} · Gap {gapSign}
                    {formatCurrency(gapAbs).replace('₹', '₹')} ({gapLabel})
                  </p>
                );
              })()}
            </div>

            {/* Key metrics */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700">Achieved</span>
                </div>
                <p className="text-lg font-semibold text-green-800">
                  {formatCurrency(targetProgress.achieved)}
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-orange-700">Remaining</span>
                </div>
                <p className="text-lg font-semibold text-orange-800">
                  {formatCurrency(targetProgress.remaining)}
                </p>
              </div>
            </div>

            {/* Days remaining */}
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">Days Remaining</span>
              </div>
              <span className="text-lg font-semibold text-gray-900">
                {targetProgress.daysLeft}
              </span>
            </div>

            {/* Exceeded badge */}
            {targetProgress.percentage >= 100 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-100 border border-green-300 rounded-lg p-3 text-center"
              >
                <span className="text-green-800 font-medium">
                  Target Exceeded by {formatCurrency(exceededAmount)}!
                </span>
              </motion.div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
